import { FC, useEffect, useState, useMemo, JSX } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import AddLicensePanelContent from "@app/admin/client-license/AddLicensePanelContent";
import { ClientLicenseDetail } from "@app/admin/client-license/ClientLicenseDetail";
import { ClientLicenseOverview, AddSoftwareLicense } from "@app/admin/client-license/clientLicenseModels";
import ClientLicenseService from "@app/admin/client-license/clientLicenseService";

interface ClientLicenseDetailsMatchProps {
  clientId?: string;
}

interface ClientLicenseDetailsState {
  clientName?: string;
}

type ClientLicenseDetailsProps = RouteComponentProps<ClientLicenseDetailsMatchProps, unknown, ClientLicenseDetailsState>;

export const ClientLicenseDetails: FC<React.PropsWithChildren<ClientLicenseDetailsProps>> = (props: ClientLicenseDetailsProps) => {

  const clientLicenseService = new ClientLicenseService();
  const urlParams = new URLSearchParams(window.location.search);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [clientLicenseOverviews, setClientLicenseOverviews] = useState<ClientLicenseOverview[]>([]);
  const [clientName, setClientName] = useState("");
  const [selectedLicense, setSelectedLicence] = useState(urlParams.get("selLicense") ?? "");

  const breadcrumbs: (string | Breadcrumb)[] = [
    {
      text: clientName
    },
  ];

  useEffect(() => {
    if (!!props.location.state && !!props.location.state.clientName) {
      setClientName(props.location.state.clientName);
    } else if (!!urlParams.get("name")) {
      setClientName(urlParams.get("name"));
    }
  }, []);

  useEffect(() => {
    fetchLicenseDetails(props.match.params.clientId);
  }, [props.match.params.clientId]);

  useEffect(() => {

    const searchParams = new URLSearchParams(window.location.search);

    if (!!clientName) {
      searchParams.set("name", clientName);
    } else {
      searchParams.delete("name");
    }
    if (!!selectedLicense) {
      searchParams.set("selLicense", selectedLicense);
    } else {
      searchParams.delete("selLicense");
    }

    history.replace(history.location.pathname + "?" + searchParams.toString());

  }, [selectedLicense, clientName]);

  const handleClientLicenseDetailChanged = () => {
    fetchLicenseDetails(props.match.params.clientId);
  }

  const licenseAccordions = useMemo(() => {
    const licenseAccordions: JSX.Element[] = [];

    if (!clientLicenseOverviews)
      return licenseAccordions;

    for (const overview of clientLicenseOverviews) {
      licenseAccordions.push(
        <Accordion
          title={`Software License ${overview.softwareLicense.licenseId}`}
          key={overview.softwareLicense.licenseId}
          defaultOpen={overview.softwareLicense.licenseId.toString() === selectedLicense}
          onPanelDidOpen={() => { setSelectedLicence(overview.softwareLicense.licenseId.toString()); }}
          onPanelDidClose={() => { setSelectedLicence(""); }}
          testId={`client-license-details__software-license-${overview.softwareLicense.licenseId}-accordion`}
        >
          <ClientLicenseDetail
            clientName={clientName}
            licenseOverview={overview}
            onClientLicenseDetailChanged={handleClientLicenseDetailChanged}
          />
        </Accordion>
      );
    }

    return licenseAccordions;

  }, [clientName, clientLicenseOverviews]);

  const fetchLicenseDetails = async (clientId: string) => {
    setLoading(true);

    const overviews = await clientLicenseService.getClientSoftwareLicenseOverviews(clientId);

    if (!!overviews) {
      for (const overview of overviews.clientLicenseOverviews) {
        if (overview.licensedProducts.length > 0) {
          setClientName(overview.licensedProducts[0].clientName);
          break;
        }
      }
    }

    setClientLicenseOverviews(overviews.clientLicenseOverviews);

    setLoading(false);
  }

  const handleAddLicense = async (sfContractId: string) => {
    setLoading(true);
    const addLicenseModel: AddSoftwareLicense = {
      clientId: parseInt(props.match.params.clientId),
      sfAccountId: "",
      sfContractId: !!sfContractId ? parseInt(sfContractId) : null
    }

    await clientLicenseService.addSoftwareLicense(addLicenseModel);
    fetchLicenseDetails(props.match.params.clientId);
    setLoading(false);
  }

  return (
    <>
      {(
        <Page documentTitle={`${clientName} - Client License - Admin`}>
          <Breadcrumbs template={BreadcrumbTemplateEnum.ClientLicenses} items={breadcrumbs} />
          <PageInner>
            <PageHeader title={clientName}>
              <PageHeaderRight>
                <FlyoutLink
                  content={{
                    body: <AddLicensePanelContent clientName={clientName} onAddLicense={handleAddLicense} />,
                    title: "Add a License",
                  }}
                  testId="client-license-details__add-license-flyout"
                  classNames="qes-button-primary mt-2"
                  size="20"
                >
                  Add
                </FlyoutLink>
              </PageHeaderRight>
            </PageHeader>
            <PageSection>
              {loading ?
                <LoadingSpinner /> :
                <>
                  {licenseAccordions.length > 0 ?
                    <AccordionContainer>
                      {licenseAccordions}
                    </AccordionContainer> :
                    <h5 className="fw-normal mt-3">No active software licenses found</h5>
                  }
                </>
              }
            </PageSection>
          </PageInner>
        </Page>
      )}
    </>
  );
}